/**
 * Repositorio de telemetría: misma frontera de migración que los demás.
 *
 * Existe porque `Home` importaba directamente de `api.js` y se saltaba la capa
 * de repositorios. El efecto era visible: la vista de cuenta leía del mock y la
 * de dispositivo leía de AWS —cuya tabla de telemetría está vacía—, así que las
 * dos vistas mostraban cosas distintas del mismo sistema.
 *
 * PUNTO DE MIGRACION: los endpoints reales ya existen y funcionan. Basta con
 * poner REACT_APP_USE_MOCKS=false cuando el dispositivo vuelva a emitir.
 */

import { mockApi, ApiError, ERROR_CODE } from '../mock/mockBackend';
import {
  getDeviceTelemetry as apiGetTelemetry,
  getAggregatedTelemetry as apiGetAggregated,
  obtenerMediciones as apiObtenerMediciones,
  getDeviceAlerts as apiGetAlerts,
  estimateWaterPressure,
} from '../api';

import { isDemoSession } from '../demoSession';

/** Fuente de datos decidida por sesión: demo -> simulado, real -> API. */
const usarSimulacion = () => isDemoSession();

function traducir(err) {
  if (err instanceof ApiError) return err;
  return new ApiError(ERROR_CODE.NETWORK, err?.message || 'Error de conexión');
}

/**
 * El backend real y la simulacion devolvian formas distintas del agregado, y
 * cada pagina se escribio contra la que tenia delante: Home contra la real,
 * Historial contra la simulada. Con una cuenta real el Historial salia vacio.
 *
 * Se unifica todo a la forma del backend real, que es el contrato que manda.
 */
function normalizarAgregado(bruto, simulated = false) {
  const vacio = { dailyData: [], summary: { totalConsumption: 0, averageDaily: 0 } };
  if (!bruto) return vacio;
  if (Array.isArray(bruto.dailyData)) return bruto; // ya viene del backend real

  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const dailyData = (bruto.data ?? []).map((d) => {
    const litros = Number(d.total_liters ?? 0);
    const minutes = simulated && d.date === todayKey
      ? Math.max(1, now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60)
      : 1440;
    return {
      date: d.date,
      consumption: litros,
      maxFlowRate: Number(d.peak_flow_lpm ?? 0),
      avgFlowRate: Number((litros / minutes).toFixed(2)),
      dataPoints: (d.hourly ?? []).length,
      hourlyBreakdown: (d.hourly ?? []).map((h) => ({
        hour: h.hour,
        consumption: Number(h.liters ?? 0),
        avgFlowRate: Number((Number(h.liters ?? 0) / 60).toFixed(2)),
        label: `${String(h.hour).padStart(2, '0')}:00`,
      })),
    };
  });

  const total = dailyData.reduce((s, d) => s + d.consumption, 0);
  return {
    ...bruto,
    dailyData,
    summary: {
      totalConsumption: Number(total.toFixed(2)),
      averageDaily: dailyData.length ? Number((total / dailyData.length).toFixed(2)) : 0,
    },
  };
}

export const telemetryRepository = {
  /** Telemetría cruda. Mantiene la forma `{data, count}` que ya espera la app. */
  async getTelemetry(deviceId, { limit = 1, hours = 24 } = {}) {
    try {
      return usarSimulacion()
        ? await mockApi.getTelemetry(deviceId, { hours, limit })
        : await apiGetTelemetry(deviceId, limit, hours);
    } catch (err) {
      throw traducir(err);
    }
  },

  /** Agregado por día para el histórico. */
  async getAggregated(deviceId, opciones = 7) {
    try {
      if (usarSimulacion()) {
        const params = typeof opciones === 'number' ? { days: opciones } : opciones;
        return normalizarAgregado(await mockApi.getAggregatedTelemetry(deviceId, params), true);
      }
      return normalizarAgregado(await apiGetAggregated(deviceId, opciones));
    } catch (err) {
      throw traducir(err);
    }
  },

  /**
   * Lectura actual: caudal, presión y consumos por período.
   * Es lo que sondea el panel; mantiene exactamente la forma que ya consumía
   * `obtenerMediciones` para no tocar los componentes.
   */
  async getCurrentReading(deviceId) {
    try {
      if (!usarSimulacion()) return await apiObtenerMediciones(deviceId);

      const device = await mockApi.getDevice(deviceId);
      if (device.last_seen === 0) {
        return {
          flow_rate_lpm: 0, daily_consumption: 0, weekly_consumption: 0,
          monthly_consumption: 0, pressure_psi: 0, valve_open: false,
        };
      }

      const abierta = device.valve_state === 'open';
      return {
        flow_rate_lpm: device.flow_rate_lpm ?? 0,
        daily_consumption: device.daily_consumption ?? 0,
        weekly_consumption: device.weekly_consumption ?? 0,
        monthly_consumption: device.monthly_consumption ?? 0,
        // Se reutiliza la estimación que ya usa la app en lugar de inventar otra.
        pressure_psi: device.pressure_psi ?? estimateWaterPressure(device.flow_rate_lpm ?? 0, abierta),
        valve_open: abierta,
        valve_state: device.valve_state,
      };
    } catch (err) {
      throw traducir(err);
    }
  },

  /** Alertas recientes del dispositivo. */
  async getAlerts(deviceId, { days = 7 } = {}) {
    try {
      return usarSimulacion()
        ? await mockApi.getDeviceAlerts(deviceId, { days })
        : await apiGetAlerts(deviceId, { days });
    } catch (err) {
      throw traducir(err);
    }
  },
};

export default telemetryRepository;
